/* eslint-disable factorial/no-raw-html */
import { Box, colors } from '@factorialco/deprecated-design-system'
import { AxisProps } from '@nivo/axes'
import { ResponsiveBar } from '@nivo/bar'

import i18n from 'modules/core/lib/i18n'

import { DetailTransformedData } from 'modules/project_management/components/charts/DistributionChart/getDistributionTransformedData'
import Legend, { LegendItem } from 'modules/project_management/components/charts/Legend'
import { ResponsiveChartProps } from 'modules/project_management/components/charts/types/responsiveChartProps'
import DetailGraphLoadingSkeleton from 'modules/project_management/components/graphs/DetailGraph/DetailGraphLoadingSkeleton'
import EmptyState from 'modules/project_management/components/graphs/EmptyState'

interface ResponsiveBarProps {
  detailData: DetailTransformedData
  unit: string
  formatYAxis?: (value: number) => string
  maxLabels: number
}

export const ResponsiveBarChart: React.FC<ResponsiveChartProps<ResponsiveBarProps>> = (props) => {
  const { detailData, unit, formatYAxis = undefined, maxLabels } = props

  const maxLabelReached = detailData.data.length > maxLabels
  const interval = Math.ceil(detailData.data.length / maxLabels)

  const computeLabel = (value: string, tickIndex: number) => {
    if (!maxLabelReached) {
      return value
    }
    return tickIndex % interval === 0 && value
  }

  const legendItems = Object.entries(detailData.keyColors).map<LegendItem>(([key, value]) => {
    return {
      id: key,
      label: key,
      color: value,
    }
  })

  const _loading = 'loading' in props && props.loading
  const _error = 'error' in props && props.error
  const axisBottom: AxisProps = {
    tickSize: 5,
    tickPadding: 5,
    tickRotation: 0,
    renderTick: ({ textAnchor, textX, textY, value, x, y, tickIndex }) => {
      return (
        <g transform={`translate(${x},${y + 8})`}>
          <text textAnchor={textAnchor} transform={`translate(${textX},${textY + 10})`}>
            <tspan>{computeLabel(value, tickIndex)}</tspan>
          </text>
        </g>
      )
    },
  }
  const axisLeft: AxisProps = {
    tickSize: 0,
    tickPadding: 14,
    tickRotation: 0,
    tickValues: 4,
    format: (value) => (formatYAxis ? formatYAxis(value) : `${value} ${unit}`),
  }

  return (
    <Box height="s300" width="full" gap="s12">
      {!_loading && _error && (
        <EmptyState
          title={i18n.t('project_management.graphs.missing_data.title')}
          description={i18n.t('project_management.graphs.missing_data.description')}
          icon="monitor-flash"
        />
      )}
      {_loading && <DetailGraphLoadingSkeleton axisBottom={axisBottom} axisLeft={axisLeft} />}
      <ResponsiveBar
        data={detailData.data}
        keys={detailData.keys}
        indexBy="label"
        margin={{ left: 68, top: 10, bottom: 34, right: 50 }}
        padding={0.5}
        valueScale={{ type: 'linear' }}
        indexScale={{ type: 'band', round: true }}
        colors={({ id }) => {
          const colorValue = detailData.keyColors[id]
          return colorValue ? colors[colorValue] : colors.grey300
        }}
        borderColor={{
          from: 'color',
        }}
        axisTop={null}
        axisRight={null}
        axisBottom={axisBottom}
        axisLeft={axisLeft}
        enableLabel={false}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={{
          from: 'color',
        }}
      />
      <>
        {Object.keys(detailData.keyColors).length < 10 && (
          <Legend
            items={legendItems}
            activeLegends={legendItems.map((legendItem) => legendItem.id)}
          />
        )}
      </>
    </Box>
  )
}
